<script>
  import {
    GraphicBlock,
    Block,
    Spinner,
  } from '@reuters-graphics/graphics-components';
  import { onMount } from 'svelte';
  import { geoNaturalEarth1, geoPath } from 'd3-geo';
  import { scaleSqrt } from 'd3-scale';
  import { max, min, ticks } from 'd3-array';
  import { feature, mesh } from 'topojson-client';
  import world from './world';
  import data from './eventCountsByLocation.json';
  import setExtents from './setExtents';

  // Props using $props() rune
  let { width = 'wider', notes, altText, view = 'world' } = $props();

  // State using $state rune
  let mapwidth = $state();
  let mapFilter = $state('');
  let mapData = $state(data);
  let mounted = $state(false);

  // Derived values using $derived rune
  let extents = $derived(
    view === 'europe' ? [-10, 30, 30, 68]
    : view === 'us' ? [-120, -3, -50, 62]
    : view === 'mideast' ? [28, 12, 69, 43]
    : [-140, -60, 175, 90]
  );

  let bbox = $derived(setExtents(...extents, 10));
  let size = $derived(view === 'world' ? [1.5, 9] : [2, 16]);
  let projection = $derived(geoNaturalEarth1().fitWidth(mapwidth, bbox));
  let path = $derived(geoPath().projection(projection));
  let bounds = $derived(geoPath(projection).bounds(bbox));
  let radius = $derived(scaleSqrt([1, max(data, (d) => +d.count)], size));

  // Calculate legend values based on the full dataset extent (not filtered data)
  let legendValues = $derived.by(() => {
    const minCount = min(data, (d) => +d.count) || 1;
    const maxCount = max(data, (d) => +d.count) || 10;
    // Get 3 nice tick values between min and max
    const tickValues = ticks(minCount, maxCount, 3);
    // Ensure we always have exactly 3 values
    if (tickValues.length < 3) {
      return [minCount, Math.round((minCount + maxCount) / 2), maxCount];
    }
    return tickValues.slice(0, 3);
  });

  let labels = $derived(
    view === 'europe' ?
      [
        {
          name: 'Italy',
          loc: [13.252945, 42.315401],
        },
        {
          name: 'Germany',
          loc: [9.727951, 51.424046],
        },
        {
          name: 'France',
          loc: [3.265686, 46.20027],
        },
        {
          name: 'UK',
          loc: [-0.301437, 52.523324],
        },
        {
          name: 'Spain',
          loc: [-3.634415, 38.93431],
        },
        {
          name: 'Morocco',
          loc: [-4.141159, 32.660188],
        },
        {
          name: 'Tunisia',
          loc: [9.633636, 32.692555],
        },
      ]
    : view === 'us' ?
      [
        {
          name: 'U.S.',
          loc: [-104.752579, 40.888262],
        },
        {
          name: 'Canada',
          loc: [-104.752579, 56.888262],
        },
        {
          name: 'Mexico',
          loc: [-102.515488, 25.359539],
        },
        {
          name: 'Colombia',
          loc: [-74.387741, 2.401284],
        },
      ]
    : view === 'mideast' ?
      [
        {
          name: 'Israel',
          loc: [34.829407, 31.45854],
        },
        {
          name: 'Egypt',
          loc: [30.779961, 23.679367],
        },
        {
          name: 'Yemen',
          loc: [49.931831, 17.11241],
        },
        {
          name: 'Turkey',
          loc: [32.868546, 38.367233],
        },
        {
          name: 'Iraq',
          loc: [42.289639, 32.838635],
        },
        {
          name: 'Iran',
          loc: [57.12616, 28.776386],
        },
        {
          name: 'Pakistan',
          loc: [65.9832, 26.270804],
        },
      ]
    : []
  );

  // Constants
  const land = feature(world, world.objects.land);
  const borders = mesh(world, world.objects.countries, (a, b) => a !== b);
  const disputedBoundaries = feature(world, world.objects.disputedBoundaries);
  // let countries = feature(world, world.objects.countries);

  const setColour = (tag) => {
    return (
      tag === 'anti-US intervention in Iran' ? '#c4320a'
      : tag === 'pro-US intervention in Iran' ? '#2d5f8d'
      : tag === 'pro-Iran' ? '#2d7a4f'
      : tag === 'anti-Iran' ? '#cc8315'
      : tag === 'mixed-US intervention in Iran' ? '#6b6b6b'
      : '#999999'
    );
  };

  // Event handlers
  const handleFilterClick = (filterType) => (e) => {
    e.preventDefault();
    if (mapFilter !== filterType) {
      mapData = data.filter((d) => d.tag === filterType);
      mapFilter = filterType;
    } else {
      mapData = data;
      mapFilter = '';
    }
  };

  onMount(() => {
    mounted = true;
  });
</script>

<GraphicBlock {width} {notes} {altText} hidden={true}>
  {#if !mounted}
    <div class="loader" style="width: 100%; height:100px">
      <Spinner />
    </div>
  {:else}
    <Block width="normal">
      <div class="legend flex">
        <div
          role="button"
          tabindex="0"
          class:active={mapFilter === 'anti-US intervention in Iran'}
          onclick={handleFilterClick('anti-US intervention in Iran')}
          onkeypress={handleFilterClick('anti-US intervention in Iran')}
          class="item border cursor-pointer text-sm font-medium px-2 py-1"
          style="color:#c4320a"
        >
          ● anti-US intervention
        </div>
        <div
          role="button"
          tabindex="0"
          class:active={mapFilter === 'pro-US intervention in Iran'}
          onclick={handleFilterClick('pro-US intervention in Iran')}
          onkeypress={handleFilterClick('pro-US intervention in Iran')}
          class="item border cursor-pointer text-sm font-medium px-2 py-1"
          style="color:#2d5f8d"
        >
          ● pro-US intervention
        </div>
        <div
          role="button"
          tabindex="0"
          class:active={mapFilter === 'pro-Iran'}
          onclick={handleFilterClick('pro-Iran')}
          onkeypress={handleFilterClick('pro-Iran')}
          class="item border cursor-pointer text-sm font-medium px-2 py-1"
          style="color:#2d7a4f"
        >
          ● pro-Iran
        </div>
        <div
          role="button"
          tabindex="0"
          class:active={mapFilter === 'anti-Iran'}
          onclick={handleFilterClick('anti-Iran')}
          onkeypress={handleFilterClick('anti-Iran')}
          class="item border cursor-pointer text-sm font-medium px-2 py-1"
          style="color:#cc8315"
        >
          ● anti-Iran
        </div>
        <!-- <div
          role="button"
          tabindex="0"
          class:active={mapFilter === 'mixed-US intervention in Iran'}
          onclick={handleFilterClick('mixed-US intervention in Iran')}
          onkeypress={handleFilterClick('mixed-US intervention in Iran')}
          class="item border cursor-pointer text-sm font-medium px-2 py-1"
          style="color:#ababab"
        >
          ● mixed
        </div> -->
      </div>
    </Block>

    <div class="map-container" bind:clientWidth={mapwidth}>
      {#if mapwidth}
        <svg
          width={Math.round(bounds[1][0])}
          height={view === 'world' ?
            Math.round(bounds[1][1]) + 10
          : Math.round(bounds[1][1]) + 40}
        >
          <defs>
            <clipPath id="mask-{view}">
              <rect
                x="0"
                y="0"
                width={Math.round(bounds[1][0])}
                height={Math.round(bounds[1][1])}
              ></rect>
            </clipPath>
          </defs>

          <!-- <path d="{path(bbox)}"></path> -->

          <g clip-path="url(#mask-{view})">
            <g id="land">
              <path
                fill="#efefef"
                stroke-linejoin="round"
                stroke-linecap="round"
                stroke="#bbb"
                stroke-width="0.5"
                d={path(land)}
              ></path>
            </g>
            <g id="borders">
              <path
                fill="none"
                stroke-linejoin="round"
                stroke-linecap="round"
                stroke="#bbb"
                stroke-width="0.5"
                d={path(borders)}
              ></path>
              <path
                fill="none"
                stroke-linejoin="round"
                stroke-linecap="round"
                stroke="#bbb"
                stroke-width="0.25"
                d={path(disputedBoundaries)}
              ></path>
            </g>
            <!-- <g id="countries">
        <path fill="#333" stroke="none" d="{path(countries)}"></path>
      </g> -->
            <g id="events">
              {#each mapData as event (event.sl)}
                {@const point = projection([event.lon, event.lat])}
                <circle
                  title="{event.location} {event.count}"
                  cx={point[0]}
                  cy={point[1]}
                  r={radius(event.count)}
                  fill-opacity="85%"
                  fill={setColour(event.tag)}
                  stroke="#fff"
                  stroke-width="0.25"
                ></circle>
              {/each}
            </g>
          </g>

          <!-- labels -->
          <g id="labels">
            {#each labels as label}
              {@const point = projection(label.loc)}
              <text
                x={point[0]}
                y={point[1]}
                dx="0"
                dy="0"
                stroke="#fff"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-xxs font-bold"
                style="text-anchor: middle; pointer-events:none;"
                fill="#fff"
                >{label.name}
              </text>
              <text
                x={point[0]}
                y={point[1]}
                dx="0"
                dy="0"
                class="text-xxs font-medium"
                style="text-anchor: middle;"
                fill="var(--theme-colour-text-primary)"
                >{label.name}
              </text>
            {/each}
          </g>

          <!-- legend -->
          <g
            id="legend"
            style="user-select: none;"
            transform="translate({view === 'world' ? mapwidth / 2 - 100 : 10} {(
              view === 'world'
            ) ?
              mapwidth < 600 ?
                Math.round(bounds[1][1])
              : Math.round(bounds[1][1]) - 30
            : Math.round(bounds[1][1]) + 20})"
          >
            {#each legendValues as value, i}
              {@const xOffset = i * 50}
              <g transform="translate({xOffset})">
                <circle
                  cx="0"
                  cy="0"
                  r={radius(value)}
                  fill="white"
                  stroke="var(--theme-colour-text-secondary)"
                  stroke-width="1.5"
                ></circle>
                <text
                  x={radius(value)}
                  y="0"
                  dx="3"
                  dy="5"
                  fill="var(--theme-colour-text-secondary)"
                  style="user-select: none;"
                  class="text-xs text-secondary"
                  >{value}{i === legendValues.length - 1 ?
                    ' demonstrations'
                  : ''}</text
                >
              </g>
            {/each}
          </g>
        </svg>
      {/if}
    </div>
  {/if}
</GraphicBlock>

<style lang="scss">
  @import '@reuters-graphics/graphics-components/dist/scss/mixins';

  #land,
  #countries {
    pointer-events: none;
  }

  .legend {
    height: 2rem;
    @include fmb-2;

    @media (max-width: 600px) {
      flex-wrap: wrap;
      max-width: 350px;
      height: 4rem;
    }

    div[role='button'] {
      user-select: none;
      height: fit-content;
      min-width: max-content;
      background-color: var(--tr-hover-background-grey);
      border: 1px solid var(--tr-muted-grey);

      @include text-sm;
      @media (max-width: 375px) {
        @include text-xs;
      }

      &:first-child {
        border-top-left-radius: 0.25rem;
        border-bottom-left-radius: 0.25rem;
        border-right-width: 0;
      }
      &:last-child {
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;
        border-left-width: 0;
      }

      @media (max-width: 600px) {
        &:nth-child(2) {
          border-top-right-radius: 0.25rem;
          border-bottom-right-radius: 0.25rem;
        }
        &:nth-child(3) {
          border-top-left-radius: 0.25rem;
          border-bottom-left-radius: 0.25rem;
        }
      }
    }

    :global(div[role='button'].active) {
      border-color: currentColor;
      outline: 1px solid currentColor;
      border-width: 1px !important;
      z-index: 1;
    }
  }
</style>
